/**
 * Recent global-search picks (需求 FR-14 §4.2.1 最近搜索 / §8 权限与数据范围).
 *
 * Its own channel rather than methods on `IModuleService`: the gated
 * `IGatedModuleService` extends that one, and a sandboxed surface must not be
 * able to read what the user has been searching for (same reasoning as
 * `LAUNCHER_CHANNEL` / `APP_LINKS_CHANNEL`).
 *
 * Kept in the host rather than renderer Web Storage — where the command MRU
 * lives — because this is user *content*, not a presentational ordering: it can
 * name people and documents, and §8 scopes it to the signed-in account and
 * current workspace, which the renderer has no way to know. The host clears it
 * on `WORKBENCH_EDITOR_SCOPE_RESET_EVENT`, the platform's only account- and
 * workspace-agnostic signal that data built against the old scope is stale.
 */

import type { IconSpec } from './index';
import type { ModuleSearchAction } from './search-contribution';

/** Cross-process channel for the recent-search store (trusted renderer only). */
export const SEARCH_RECENT_CHANNEL = 'search-recent';

/**
 * How many picks are kept. Faces show fewer (the header dropdown shows 5, 需求
 * §4.2.1); the surplus is what keeps the list populated when entries no longer
 * resolve — a command whose module was uninstalled is dropped at read time.
 */
export const SEARCH_RECENT_LIMIT = 20;

/**
 * What a row looked like when it was picked — the fallback half of
 * {@link SearchRecentEntry}.
 *
 * Only carries what can survive being written to disk, which is also the rule
 * for *when* to store one: an action that is a closure cannot be replayed from
 * storage. That lines up exactly with who needs a snapshot — the platform's own
 * providers use closures and resolve fine live; a module's hits are declarative
 * (`command` / `openView`) and are the ones that cannot be resolved at all.
 */
export interface SearchRecentSnapshot {
  readonly title: string;
  readonly subtitle?: string;
  readonly icon?: IconSpec;
  readonly badge?: string;
  /** Same `personId` as a search hit. Kept so a replayed row can still be adorned. */
  readonly personId?: string;
  /** Replayed as-is when the row is chosen again. Must be serializable. */
  readonly action: ModuleSearchAction;
}

/**
 * One remembered pick: a **stable identity**, plus a snapshot for the rows whose
 * identity cannot be resolved back.
 *
 * The identity is what §9 asks for, and it stays the preferred source: titles
 * are `%key%` placeholders resolved against a live catalog, icons get swapped,
 * and commands disappear with their module, so a live re-read is always more
 * correct. The face therefore looks the entry up in its provider **first**.
 *
 * The snapshot exists because that lookup cannot work for a module's content.
 * A section's provider crosses a process boundary (so it answers async, and the
 * recent section has to paint on the first frame) and it answers *queries*, not
 * "what is row X" — asking it with an empty query yields nothing by design.
 * Without a snapshot every conversation, message and group the user opened was
 * silently dropped at render, which is the state this replaced.
 *
 * The cost is accepted and bounded: a renamed group shows its old name until
 * the section grows a real by-id lookup, at which point the identity is still
 * here to do it properly.
 */
export interface SearchRecentEntry {
  /** Provider that produced the row (`command`, `app`, …). */
  readonly providerId: string;
  /** The row's `SearchResult.key`, unique within that provider. */
  readonly key: string;
  readonly recordedAt: number;
  /** Present only for rows their provider cannot resolve back (见 above). */
  readonly snapshot?: SearchRecentSnapshot;
}

export interface ISearchRecentService {
  /** Most recent first. */
  list(): Promise<readonly SearchRecentEntry[]>;
  /**
   * Record a pick, moving an existing one to the front. `snapshot` is kept for
   * rows whose provider cannot resolve them later (见 {@link SearchRecentEntry}).
   */
  record(
    providerId: string,
    key: string,
    snapshot?: SearchRecentSnapshot,
  ): Promise<void>;
  clear(): Promise<void>;
}
